/**
 * @ Author: Housefolios
 * @ Create Time: 2025-01-27 00:00:00
 * @ Modified by: AI Assistant
 * @ Modified time: 2025-01-27 00:00:00
 * @ Description: Custom hook for delete confirmation dialogs
 */

import { useState } from 'react'

/**
 * Custom hook for managing delete confirmation dialog state and logic
 *
 * @param {Object} options
 * @param {Function} options.onDelete - The actual delete function to call
 * @param {string} options.successMessage - Success message to show
 * @param {string} options.errorMessage - Error message to show
 * @param {Function} [options.onSuccess] - Optional callback after successful deletion
 * @param {Function} [options.onError] - Optional callback after failed deletion
 * @returns {Object} - { open, loading, openDialog, closeDialog, handleDelete }
 */
export function useDeleteConfirmation({
  onDelete,
  successMessage = 'Item deleted successfully',
  errorMessage = 'Failed to delete item',
  onSuccess = null,
  onError = null,
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const openDialog = () => {
    setOpen(true)
  }

  const closeDialog = () => {
    setOpen(false)
    setLoading(false)
  }

  const handleDelete = async () => {
    setLoading(true)
    try {
      await onDelete()
      if (onSuccess) onSuccess()
    } catch (error) {
      if (onError) onError(error)
      throw error // Re-throw so the dialog can handle the error
    } finally {
      setLoading(false)
    }
  }

  return {
    open,
    loading,
    openDialog,
    closeDialog,
    handleDelete,
  }
}
